/**
 * custom-product-configurator.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Vanilla JS product configurator for Shopify Dawn theme.
 * No framework dependencies.
 *
 * Responsibilities:
 *  - Maintain a single state object (garment / decoration / color / size)
 *  - Render color swatches and size buttons dynamically from PRODUCT_CONFIG
 *  - Update title, price, images, availability on state change
 *  - Sync Shopify variant ID to hidden form input
 *  - Sync URL ?variant= param via History API
 *  - Support deep-link initialization (load state from URL on page load)
 *  - Handle browser back/forward navigation
 *  - Integrate with Dawn's cart drawer via AJAX
 *  - Handle quantity selector
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ─── Wait for DOM + config ────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', init);

  // ─── State ────────────────────────────────────────────────────────────────
  const DEFAULT_STATE = {
    garment:    'hoodie',
    decoration: 'printed',
    color:      'Pigment Light Blue',
    size:       'M',
  };

  let state = { ...DEFAULT_STATE };

  // Currently displayed image index within the active set
  let activeImageIndex = 0;

  // ─── DOM refs (resolved once, reused) ────────────────────────────────────
  const dom = {};

  function resolveDOM() {
    dom.title         = document.getElementById('cpc-title');
    dom.price         = document.getElementById('cpc-price');
    dom.comparePrice  = document.getElementById('cpc-compare-price');
    dom.variantInput  = document.getElementById('cpc-variant-id');
    dom.atcBtn        = document.getElementById('cpc-atc-btn');
    dom.atcText       = document.getElementById('cpc-atc-text');
    dom.atcForm       = document.getElementById('cpc-atc-form');
    dom.availability  = document.getElementById('cpc-availability');
    dom.soldOutBadge  = document.getElementById('cpc-sold-out-badge');
    dom.colorGroup    = document.getElementById('cpc-color-group');
    dom.colorLabel    = document.getElementById('cpc-color-label');
    dom.sizeGroup     = document.getElementById('cpc-size-group');
    dom.thumbs        = document.getElementById('cpc-thumbs');
    dom.mainWrap      = document.getElementById('cpc-main-image-wrap');
    dom.qtyInput      = document.getElementById('cpc-qty');
    dom.qtyMinus      = document.getElementById('cpc-qty-minus');
    dom.qtyPlus       = document.getElementById('cpc-qty-plus');
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  /** Format cents → "$XX.XX" matching Shopify money format */
  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /** Return the PRODUCT_CONFIG block for current garment + decoration */
  function currentBlock() {
    return window.PRODUCT_CONFIG[state.garment]?.[state.decoration];
  }

  /** Return all image URLs for current garment/deco/color */
  function currentImages() {
    const block = currentBlock();
    return block?.images?.[state.color] ?? [];
  }

  /** Return availability for current color + size */
  function isAvailable(color, size) {
    const block = currentBlock();
    return block?.availability?.[color]?.[size] !== false;
  }

  /** Return variant ID for current full state */
  function currentVariantId() {
    const block = currentBlock();
    return block?.variants?.[state.color]?.[state.size] ?? null;
  }

  /** Return price for current full state */
  function currentPrice() {
    const block = currentBlock();
    return block?.prices?.[state.color]?.[state.size] ?? null;
  }

  // ─── Color swatch rendering ───────────────────────────────────────────────

  function renderColorSwatches() {
    const block = currentBlock();
    if (!block) return;

    dom.colorGroup.innerHTML = '';

    block.colors.forEach((color) => {
      const btn    = document.createElement('button');
      const hex    = window.CPC_COLOR_SWATCHES[color] || '#888';
      const isActive = color === state.color;

      btn.type      = 'button';
      btn.className = 'cpc-color-btn' + (isActive ? ' is-active' : '');
      btn.dataset.color = color;
      btn.setAttribute('aria-label', color);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      btn.title = color;

      btn.innerHTML = `<span class="cpc-swatch" style="background-color:${hex}"></span>`;
      btn.addEventListener('click', onColorClick);
      dom.colorGroup.appendChild(btn);
    });
  }

  function onColorClick(e) {
    const color = e.currentTarget.dataset.color;
    if (color === state.color) return;
    state.color = color;
    ensureValidSize();
    updateAll();
  }

  // ─── Size button rendering ─────────────────────────────────────────────────

  function renderSizeButtons() {
    const block = currentBlock();
    if (!block) return;

    dom.sizeGroup.innerHTML = '';

    block.sizes.forEach((size) => {
      const avail    = isAvailable(state.color, size);
      const isActive = size === state.size;

      const btn = document.createElement('button');
      btn.type  = 'button';
      btn.className = [
        'cpc-size-btn',
        isActive ? 'is-active' : '',
        !avail  ? 'is-unavailable' : '',
      ].filter(Boolean).join(' ');
      btn.dataset.size = size;
      btn.textContent  = size;
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      btn.setAttribute('aria-label', avail ? size : `${size} — sold out`);
      if (!avail) btn.setAttribute('aria-disabled', 'true');

      btn.addEventListener('click', onSizeClick);
      dom.sizeGroup.appendChild(btn);
    });
  }

  function onSizeClick(e) {
    const btn  = e.currentTarget;
    const size = btn.dataset.size;
    if (btn.classList.contains('is-unavailable')) return;
    if (size === state.size) return;
    state.size = size;
    updateAll({ skipImages: true });
  }

  /** Ensure currently selected size is valid for the current color */
  function ensureValidSize() {
    const block = currentBlock();
    if (!block) return;
    const available = block.sizes.filter(s => isAvailable(state.color, s));
    if (!available.includes(state.size)) {
      state.size = available[0] ?? block.sizes[0];
    }
  }

  // ─── Image gallery ────────────────────────────────────────────────────────

  /**
   * Builds a fresh set of <img> elements and injects them into the main wrap
   * and thumbnail strip. Called whenever garment / deco / color changes.
   *
   * Images use display:block + height:auto via CSS for natural proportions —
   * no fixed container aspect-ratio means zero letterboxing.
   */
  function renderImages() {
    const images = currentImages();
    activeImageIndex = 0;

    // ── Main images ──
    const existing = dom.mainWrap.querySelectorAll('.cpc-main-image');
    existing.forEach(el => el.remove());

    images.forEach((url, i) => {
      const div = document.createElement('div');
      div.className    = 'cpc-main-image' + (i === 0 ? ' is-active' : '');
      div.dataset.index = i;

      const img        = document.createElement('img');
      img.src          = url;
      img.alt          = `Product image ${i + 1}`;
      img.loading      = i === 0 ? 'eager' : 'lazy';
      img.decoding     = i === 0 ? 'sync' : 'async';

      div.appendChild(img);
      dom.mainWrap.insertBefore(div, dom.soldOutBadge);
    });

    // ── Thumbnails ──
    dom.thumbs.innerHTML = '';

    images.forEach((url, i) => {
      const btn        = document.createElement('button');
      btn.type         = 'button';
      btn.className    = 'cpc-thumb' + (i === 0 ? ' is-active' : '');
      btn.dataset.index = i;
      btn.setAttribute('aria-label', `View image ${i + 1}`);
      btn.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');

      const img    = document.createElement('img');
      img.src      = url;
      img.alt      = `Thumbnail ${i + 1}`;
      img.loading  = 'lazy';
      img.decoding = 'async';
      img.width    = 160;
      img.height   = 160;

      btn.appendChild(img);
      btn.addEventListener('click', onThumbClick);
      dom.thumbs.appendChild(btn);
    });
  }

  function onThumbClick(e) {
    const idx = parseInt(e.currentTarget.dataset.index, 10);
    setActiveImage(idx);
  }

  function setActiveImage(idx) {
    // Deactivate previous
    const prevMain  = dom.mainWrap.querySelector('.cpc-main-image.is-active');
    const prevThumb = dom.thumbs.querySelector('.cpc-thumb.is-active');
    if (prevMain)  prevMain.classList.remove('is-active');
    if (prevThumb) {
      prevThumb.classList.remove('is-active');
      prevThumb.setAttribute('aria-pressed', 'false');
    }

    // Activate new
    const nextMain  = dom.mainWrap.querySelector(`.cpc-main-image[data-index="${idx}"]`);
    const nextThumb = dom.thumbs.querySelector(`.cpc-thumb[data-index="${idx}"]`);
    if (nextMain)  nextMain.classList.add('is-active');
    if (nextThumb) {
      nextThumb.classList.add('is-active');
      nextThumb.setAttribute('aria-pressed', 'true');
    }

    activeImageIndex = idx;

    // Ensure the newly active thumbnail is visible within the scroll window.
    // 'nearest' moves the minimum amount needed — it won't jump if already visible.
    if (nextThumb) {
      nextThumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
  }

  // ─── Title ────────────────────────────────────────────────────────────────

  function renderTitle() {
    const block = currentBlock();
    if (dom.title && block) dom.title.textContent = block.title;
  }

  // ─── Price ────────────────────────────────────────────────────────────────

  function renderPrice() {
    const price = currentPrice();
    if (price !== null && dom.price) {
      dom.price.textContent = formatMoney(price);
    }
    if (dom.comparePrice) dom.comparePrice.textContent = '';
  }

  // ─── Color label ─────────────────────────────────────────────────────────

  function renderColorLabel() {
    if (dom.colorLabel) dom.colorLabel.textContent = state.color;
  }

  // ─── Variant ID + availability ────────────────────────────────────────────

  function renderVariantAndAvailability() {
    const vid   = currentVariantId();
    const avail = isAvailable(state.color, state.size);

    if (dom.variantInput) {
      dom.variantInput.value = vid ?? '';
    }

    if (dom.atcBtn && dom.atcText) {
      if (!vid || vid === 'REPLACE_WITH_VARIANT_ID') {
        dom.atcBtn.disabled = true;
        dom.atcText.textContent = 'Unavailable';
      } else if (!avail) {
        dom.atcBtn.disabled = true;
        dom.atcText.textContent = 'Sold Out';
      } else {
        dom.atcBtn.disabled = false;
        dom.atcText.textContent = 'Add to Cart';
      }
    }

    if (dom.soldOutBadge) {
      dom.soldOutBadge.hidden = avail;
    }

    if (dom.availability) {
      dom.availability.textContent = '';
      dom.availability.className   = 'cpc-availability';
    }
  }

  // ─── Toggle active states on existing buttons ─────────────────────────────

  function syncToggleActiveStates() {
    document.querySelectorAll('[data-group="garment"]').forEach(btn => {
      const active = btn.dataset.value === state.garment;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    document.querySelectorAll('[data-group="decoration"]').forEach(btn => {
      const active = btn.dataset.value === state.decoration;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    dom.colorGroup?.querySelectorAll('.cpc-color-btn').forEach(btn => {
      const active = btn.dataset.color === state.color;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    dom.sizeGroup?.querySelectorAll('.cpc-size-btn').forEach(btn => {
      const active = btn.dataset.size === state.size;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  // ─── URL sync ─────────────────────────────────────────────────────────────

  function syncURL(replace = false) {
    const vid = currentVariantId();
    if (!vid || vid === 'REPLACE_WITH_VARIANT_ID') return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', vid);

    const historyState = { ...state, variantId: vid };

    if (replace) {
      window.history.replaceState(historyState, '', url.toString());
    } else {
      window.history.pushState(historyState, '', url.toString());
    }
  }

  // ─── Deep-link initialization ─────────────────────────────────────────────

  function initFromURL() {
    const params = new URLSearchParams(window.location.search);
    const vid    = params.get('variant');

    if (vid && window.CPC_VARIANT_LOOKUP && window.CPC_VARIANT_LOOKUP[vid]) {
      state = { ...window.CPC_VARIANT_LOOKUP[vid] };
    } else {
      state = { ...DEFAULT_STATE };
    }
  }

  // Handle browser back/forward
  window.addEventListener('popstate', (e) => {
    if (e.state && e.state.garment) {
      state = {
        garment:    e.state.garment,
        decoration: e.state.decoration,
        color:      e.state.color,
        size:       e.state.size,
      };
      updateAll({ skipURL: true });
    } else {
      initFromURL();
      updateAll({ skipURL: true });
    }
  });

  // ─── Master update ────────────────────────────────────────────────────────

  /**
   * updateAll — re-renders everything that depends on current state.
   *
   * Options:
   *   skipImages {boolean}  — true when only size changed (no image refresh needed)
   *   skipURL    {boolean}  — true when called from popstate (already in sync)
   *   replace    {boolean}  — true on first render (replaceState not pushState)
   */
  function updateAll({ skipImages = false, skipURL = false, replace = false } = {}) {
    renderTitle();
    renderPrice();
    renderColorLabel();
    renderColorSwatches();
    renderSizeButtons();
    syncToggleActiveStates();
    renderVariantAndAvailability();

    if (!skipImages) {
      renderImages();
    }

    if (!skipURL) {
      syncURL(replace);
    }
  }

  // ─── Garment / Decoration toggle listeners ────────────────────────────────

  /**
   * Ensures state.color is valid for the current garment + decoration block.
   * Called any time either toggle changes before updateAll().
   *
   * Root cause of the blank-screen bug: the decoration listener was calling
   * ensureValidSize() but NOT ensureValidColor(). When switching to a
   * decoration whose color list doesn't include the currently selected color
   * (e.g. T-Shirt Embroidery has no "Pigment Light Blue"), currentImages()
   * returned [] and currentVariantId() returned null — producing blank mockups
   * and an "Unavailable" ATC button. The garment listener already had this
   * check inline; it's now a shared function so both listeners use it.
   */
  function ensureValidColor() {
    const block  = currentBlock();
    const colors = block?.colors ?? [];
    if (colors.length && !colors.includes(state.color)) {
      state.color = colors[0];
    }
  }

  function attachToggleListeners() {
    document.querySelectorAll('[data-group="garment"]').forEach(btn => {
      btn.addEventListener('click', () => {
        const val = btn.dataset.value;
        if (val === state.garment) return;
        state.garment = val;
        ensureValidColor();
        ensureValidSize();
        updateAll();
      });
    });

    document.querySelectorAll('[data-group="decoration"]').forEach(btn => {
      btn.addEventListener('click', () => {
        const val = btn.dataset.value;
        if (val === state.decoration) return;
        state.decoration = val;
        ensureValidColor();  // ← this was the missing call
        ensureValidSize();
        updateAll();
      });
    });
  }

  // ─── Quantity selector ────────────────────────────────────────────────────

  function attachQtyListeners() {
    dom.qtyMinus?.addEventListener('click', () => {
      const current = parseInt(dom.qtyInput.value, 10) || 1;
      if (current > 1) dom.qtyInput.value = current - 1;
    });

    dom.qtyPlus?.addEventListener('click', () => {
      const current = parseInt(dom.qtyInput.value, 10) || 1;
      if (current < 99) dom.qtyInput.value = current + 1;
    });

    dom.qtyInput?.addEventListener('change', () => {
      let val = parseInt(dom.qtyInput.value, 10);
      if (isNaN(val) || val < 1) val = 1;
      if (val > 99) val = 99;
      dom.qtyInput.value = val;
    });
  }

  // ─── Thumbnail strip height sync ─────────────────────────────────────────
  /**
   * Locks the thumbnail strip's max-height to the exact rendered height of
   * the main image wrap, so the strip is always a tidy scrollable window
   * — never taller or shorter than the image beside it.
   *
   * ResizeObserver fires automatically whenever the image wrap changes height:
   *  - first image paints
   *  - user switches variant (different image dimensions)
   *  - browser window is resized
   * No manual call-sites needed anywhere else.
   *
   * On mobile the `max-height: none !important` in the CSS overrides whatever
   * value JS writes here, so this is effectively a no-op on narrow screens.
   */
  function attachThumbHeightSync() {
    if (!dom.mainWrap || !dom.thumbs) return;

    function sync() {
      requestAnimationFrame(() => {
        const h = dom.mainWrap.offsetHeight;
        if (h > 0) dom.thumbs.style.maxHeight = h + 'px';
      });
    }

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(sync);
      ro.observe(dom.mainWrap);
    }

    // Fallback for older browsers: re-sync on window resize
    window.addEventListener('resize', sync, { passive: true });

    // Initial pass — after first paint so the image has a real height
    requestAnimationFrame(sync);
  }

  // ─── Swipe navigation on main image ──────────────────────────────────────
  /**
   * Detects horizontal swipe on the main image wrap and advances/retreats
   * through the current image set.
   *
   * Guard conditions:
   *  - Minimum horizontal travel of 40px to count as a swipe
   *  - Horizontal delta must exceed vertical delta — prevents triggering
   *    during normal page scroll
   *  - Only fires when more than one image is available
   */
  function attachSwipeListener() {
    if (!dom.mainWrap) return;

    const THRESHOLD = 40; // px minimum horizontal travel
    let startX = 0;
    let startY = 0;

    dom.mainWrap.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    dom.mainWrap.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;

      // Not a swipe: too short, or more vertical than horizontal
      if (Math.abs(dx) < THRESHOLD || Math.abs(dy) > Math.abs(dx)) return;

      const total = currentImages().length;
      if (total <= 1) return;

      if (dx < 0) {
        // Swipe left → advance to next image (wraps around)
        setActiveImage((activeImageIndex + 1) % total);
      } else {
        // Swipe right → go back to previous image (wraps around)
        setActiveImage((activeImageIndex - 1 + total) % total);
      }
    }, { passive: true });
  }

  // ─── Add to Cart (AJAX / Dawn cart drawer) ───────────────────────────────

  function attachATCListener() {
    dom.atcForm?.addEventListener('submit', onATCSubmit);
  }

  // ─── Pet's Name input ─────────────────────────────────────────────────────

function attachPetNameListeners() {
  const input   = document.getElementById('cpc-pet-name');
  const counter = document.getElementById('cpc-pet-name-counter');
  const error   = document.getElementById('cpc-pet-name-error');
  if (!input) return;

  function updateCounter() {
    const len = input.value.length;
    const max = 12;
    counter.textContent = len > 0 ? `${len} / ${max}` : '';
    counter.classList.toggle('is-near', len >= max - 2 && len < max);
    counter.classList.toggle('is-full', len >= max);
  }

  function showError() {
    input.classList.add('is-error', 'is-shake');
    error.classList.add('is-visible');
    input.focus();
    input.addEventListener('animationend', () => {
      input.classList.remove('is-shake');
    }, { once: true });
  }

  function hideError() {
    input.classList.remove('is-error');
    error.classList.remove('is-visible');
  }

  input.addEventListener('input', () => {

  // Allow only letters, spaces, hyphens, apostrophes
  const clean = input.value.replace(/[^a-zA-Z\s'\-]/g, '');

  if (clean !== input.value) {
    const pos = input.selectionStart;
    input.value = clean;
    input.setSelectionRange(pos, pos);
  }

  // UPDATE COUNTER LIVE
  updateCounter();

  // Remove errors while typing
  hideError();
});

input.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') {

    // STOP FORM SUBMIT
    e.preventDefault();
    e.stopPropagation();

    if (input.value.trim()) {
      input.classList.remove('is-error');
      error.classList.remove('is-visible');

      input.classList.add('input-success');

      // optional: close keyboard on mobile
      input.blur();

    } else {
      input.classList.remove('input-success');
      showError();
    }
  }
});

  input.addEventListener('focus', hideError);

  input.addEventListener('blur', () => {
    input.value = input.value.trim();
    updateCounter();
  });

  

  // Expose validator so onATCSubmit can call it
  window._cpcValidatePetName = function () {
  if (!input.value.trim()) {
    showError();
    input.classList.remove('input-success');
    return false;
  }

  return true;
};
}

 function attachFileUploadListeners() {
    const input   = document.getElementById('cpc-file-upload');
    const btn     = document.getElementById('cpc-file-btn');
    const label   = document.getElementById('cpc-file-name');
    const error   = document.getElementById('cpc-file-error');
    let uploadedImageURL = '';
    const preview    = document.getElementById('cpc-file-preview');
    const previewImg = document.getElementById('cpc-file-preview-img');
    if (!input || !btn) return;
 
    btn.addEventListener('click', () => input.click());
 
    input.addEventListener('change', () => {

  const files = input.files;

  if (!files.length) return;

  const file = files[0];

  label.textContent = file.name;

  label.classList.add('is-filled');
  btn.classList.add('is-filled');

  btn.classList.remove('is-error');
  error.classList.remove('is-visible');

  // IMAGE PREVIEW
  if (file.type.startsWith('image/')) {

    const reader = new FileReader();

    reader.onload = function(e) {
      uploadedImageURL = e.target.result;
      previewImg.src = uploadedImageURL;
      preview.hidden = false;
    };

    // ─── Click preview → open fullscreen ─────────────────

preview.addEventListener('click', () => {

  if (!uploadedImageURL) return;

  let modal = document.querySelector('.cpc-upload-lightbox');

  // Create once
  if (!modal) {

    modal = document.createElement('div');

    modal.className = 'cpc-upload-lightbox';

    modal.innerHTML = `
      <button class="cpc-upload-lightbox-close" aria-label="Close preview">
        ✕
      </button>

      <img src="" alt="Uploaded design preview fullscreen">
    `;

    document.body.appendChild(modal);

    // Close when clicking backdrop
    modal.addEventListener('click', (e) => {
      if (
        e.target === modal ||
        e.target.classList.contains('cpc-upload-lightbox-close')
      ) {
        modal.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    });
  }

  modal.querySelector('img').src = uploadedImageURL;

  modal.classList.add('is-open');

  document.body.style.overflow = 'hidden';
});

    reader.readAsDataURL(file);
  }
});
 
    // Expose validator for onATCSubmit
    window._cpcValidateFileUpload = function () {
      if (!input.files.length) {
        btn.classList.add('is-error');
        error.classList.add('is-visible');
        
        btn.focus();
        return false;
      }
      return true;
    };
  }


  function onATCSubmit(e) {
    e.preventDefault();

    const vid = dom.variantInput?.value;
    if (!vid || vid === 'REPLACE_WITH_VARIANT_ID') return;
    if (typeof window._cpcValidatePetName === 'function' && !window._cpcValidatePetName()) return;
    if (typeof window._cpcValidateFileUpload === 'function' && !window._cpcValidateFileUpload()) return;

    const petNameEl = document.getElementById('cpc-pet-name');
    const fileInput = document.getElementById('cpc-file-upload');

    // ── Inject properties as hidden inputs into Dawn's own form ──
    // then submit it natively so Dawn handles the cart notification itself.
    const form = dom.atcForm;

    // Remove any previously injected inputs
    form.querySelectorAll('.cpc-injected').forEach(el => el.remove());

    function inject(name, value) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.className = 'cpc-injected';
      form.appendChild(input);
    }

    inject('properties[Pet Name]', petNameEl?.value?.trim() || '');

    // For the file, we still need AJAX since native form submit can't attach
    // a file property the way Shopify expects. So only use native submit when
    // there's no file, otherwise fall through to FormData AJAX below.
    const file = fileInput?.files?.[0];

    if (!file) {
      // No file — let Dawn's form submit natively, full notification works
      dom.atcBtn.classList.add('is-loading');
      dom.atcBtn.disabled = true;

      // Dawn's form submit listener will re-enable the button
      form.removeEventListener('submit', onATCSubmit);
      form.requestSubmit(dom.atcBtn);

      // Re-attach after a tick
      setTimeout(() => {
        form.addEventListener('submit', onATCSubmit);
        dom.atcBtn.classList.remove('is-loading');
        dom.atcBtn.disabled = false;
      }, 3000);
      return;
    }

    // Has file — must use FormData AJAX
dom.atcBtn.classList.add('is-loading');
dom.atcBtn.disabled = true;

const formData = new FormData();
formData.append('items[0][id]', parseInt(vid, 10));
formData.append('items[0][quantity]', parseInt(dom.qtyInput?.value, 10) || 1);
formData.append('items[0][properties][Pet Name]', petNameEl?.value?.trim() || '');
formData.append('items[0][properties][Your label]', file, file.name);

// Ask Shopify to return pre-rendered section HTML in the same response
// so Dawn's renderContents() has what it needs to populate the notification.
const notifProductEl = document.getElementById('cart-notification-product');
const notifButtonEl  = document.getElementById('cart-notification-button');
const sectionIds     = [notifProductEl?.dataset?.id, notifButtonEl?.dataset?.id].filter(Boolean);
if (sectionIds.length) {
  formData.append('sections',     sectionIds.join(','));
  formData.append('sections_url', window.location.pathname);
}

fetch(window.Shopify?.routes?.root + 'cart/add.js', {
  method:  'POST',
  headers: { Accept: 'application/json' },
  body:    formData,
})
  .then(res => res.json())
  .then(data => {
    dom.atcBtn.classList.remove('is-loading');
    dom.atcBtn.disabled = false;

    if (data.status === 422 || data.description) {
      showATCFeedback(data.description || 'Item unavailable', false);
      return;
    }

    // ── Open Dawn's cart notification ──────────────────────────────
    // Wrapped in try/catch: if renderContents() throws (e.g. sections
    // returned empty), it previously escaped into .catch and showed
    // "Error — please try again" even though the item was added fine.
    try {
      const notification = document.querySelector('cart-notification');
      const hasSections  = data.sections && Object.keys(data.sections).length > 0;

      if (notification && typeof notification.renderContents === 'function' && hasSections) {
        notification.renderContents({ id: parseInt(vid, 10), sections: data.sections });
      } else if (notification && typeof notification.open === 'function') {
        notification.open();
      } else {
        showATCFeedback('Added to cart!', true);
      }
    } catch (notifErr) {
      console.warn('[CPC] Notification render error (item was added):', notifErr);
      showATCFeedback('Added to cart!', true);
    }

    // ── Update cart count bubble ───────────────────────────────────
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(r => r.json())
      .then(cart => {
        document.querySelectorAll('.cart-count-bubble').forEach(el => {
          el.classList.remove('hidden');
          const num = el.querySelector('[aria-hidden="true"]');
          if (num) num.textContent = cart.item_count;
        });
      })
      .catch(() => {});
  })
  .catch(err => {
    console.error('[CPC] Add to cart error:', err);
    dom.atcBtn.classList.remove('is-loading');
    dom.atcBtn.disabled = false;
    showATCFeedback('Error — please try again', false);
  });
  }

  function showATCFeedback(msg, success) {
    if (!dom.availability) return;
    dom.availability.textContent = msg;
    dom.availability.className   = 'cpc-availability ' + (success ? 'is-success' : 'is-error');
    setTimeout(() => {
      dom.availability.textContent = '';
      dom.availability.className   = 'cpc-availability';
    }, 3000);
  }

  // ─── Lightbox ─────────────────────────────────────────────────────────────
  /**
   * Full-screen image viewer, opened when the user clicks/taps the active
   * mockup image.
   *
   * Features:
   *  - Fade + scale entry/exit animation
   *  - Prev / Next navigation (arrows + left/right swipe inside lightbox)
   *  - Keyboard: Escape to close, ← → to navigate
   *  - Swipe-down on touch (≥80px vertical) to dismiss
   *  - Counter pill showing current position ("3 / 7")
   *  - body scroll locked while open
   *  - Click backdrop to close
   *  - Focus moves to close button on open; returns to the image wrap on close
   */

  let lbEl        = null;  // .cpc-lightbox
  let lbImgEl     = null;  // .cpc-lightbox__img
  let lbCounter   = null;  // .cpc-lightbox__counter
  let lbPrev      = null;  // prev arrow button
  let lbNext      = null;  // next arrow button
  let lbIndex     = 0;     // which image is currently shown in lightbox
  let lbImages    = [];    // [{src, alt}] for the current variant
  let lbIsOpen    = false;

  function buildLightbox() {
    if (lbEl) return;

    lbEl = document.createElement('div');
    lbEl.className = 'cpc-lightbox';
    lbEl.setAttribute('role', 'dialog');
    lbEl.setAttribute('aria-modal', 'true');
    lbEl.setAttribute('aria-label', 'Product image fullscreen view');
    lbEl.hidden = true;

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'cpc-lightbox__close';
    closeBtn.setAttribute('aria-label', 'Close fullscreen view');
    closeBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>`;
    closeBtn.addEventListener('click', closeLightbox);

    // Prev arrow
    lbPrev = document.createElement('button');
    lbPrev.className = 'cpc-lightbox__nav cpc-lightbox__nav--prev';
    lbPrev.setAttribute('aria-label', 'Previous image');
    lbPrev.innerHTML = `<svg width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true"><path d="M8 2L2 8L8 14" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    lbPrev.addEventListener('click', (e) => { e.stopPropagation(); lbNavigate(-1); });

    // Next arrow
    lbNext = document.createElement('button');
    lbNext.className = 'cpc-lightbox__nav cpc-lightbox__nav--next';
    lbNext.setAttribute('aria-label', 'Next image');
    lbNext.innerHTML = `<svg width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true"><path d="M2 2L8 8L2 14" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    lbNext.addEventListener('click', (e) => { e.stopPropagation(); lbNavigate(1); });

    // Image inner wrapper
    const inner = document.createElement('div');
    inner.className = 'cpc-lightbox__inner';

    lbImgEl = document.createElement('img');
    lbImgEl.className = 'cpc-lightbox__img';
    lbImgEl.alt = '';
    inner.appendChild(lbImgEl);

    // Counter
    lbCounter = document.createElement('div');
    lbCounter.className = 'cpc-lightbox__counter';
    lbCounter.setAttribute('aria-live', 'polite');

    lbEl.appendChild(closeBtn);
    lbEl.appendChild(lbPrev);
    lbEl.appendChild(lbNext);
    lbEl.appendChild(inner);
    lbEl.appendChild(lbCounter);
    document.body.appendChild(lbEl);

    // Click backdrop (not inner content) to close
    lbEl.addEventListener('click', (e) => {
      if (e.target === lbEl) closeLightbox();
    });

    // Keyboard: Escape closes, arrows navigate
    document.addEventListener('keydown', (e) => {
      if (!lbIsOpen) return;
      if (e.key === 'Escape')      closeLightbox();
      if (e.key === 'ArrowLeft')   lbNavigate(-1);
      if (e.key === 'ArrowRight')  lbNavigate(1);
    });

    // Touch: swipe-down to close, horizontal swipe to navigate
    let lbTouchStartX = 0;
    let lbTouchStartY = 0;
    lbEl.addEventListener('touchstart', (e) => {
      lbTouchStartX = e.touches[0].clientX;
      lbTouchStartY = e.touches[0].clientY;
    }, { passive: true });
    lbEl.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - lbTouchStartX;
      const dy = e.changedTouches[0].clientY - lbTouchStartY;
      // Swipe down ≥80px → close
      if (dy > 80 && Math.abs(dy) > Math.abs(dx)) {
        closeLightbox();
        return;
      }
      // Horizontal swipe ≥40px → navigate
      if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy)) {
        lbNavigate(dx < 0 ? 1 : -1);
      }
    }, { passive: true });
  }

  function lbNavigate(dir) {
    if (lbImages.length <= 1) return;
    lbIndex = (lbIndex + dir + lbImages.length) % lbImages.length;
    lbShowImage(lbIndex);
    // Keep the main gallery in sync
    setActiveImage(lbIndex);
  }

  function lbShowImage(idx) {
    const item = lbImages[idx];
    if (!item || !lbImgEl) return;
    lbImgEl.src = item.src;
    lbImgEl.alt = item.alt;

    // Counter
    if (lbImages.length > 1) {
      lbCounter.textContent = `${idx + 1} / ${lbImages.length}`;
      lbCounter.hidden = false;
      lbPrev.hidden   = false;
      lbNext.hidden   = false;
    } else {
      lbCounter.hidden = true;
      lbPrev.hidden   = true;
      lbNext.hidden   = true;
    }
  }

  function openLightbox(startIdx) {
    buildLightbox();

    // Collect full-resolution sources from rendered images in the main wrap
    const imgs = Array.from(
      dom.mainWrap.querySelectorAll('.cpc-main-image img')
    );
    lbImages = imgs.map(img => ({ src: img.src, alt: img.alt }));
    lbIndex  = startIdx ?? activeImageIndex;

    lbShowImage(lbIndex);
    lbEl.hidden = false;

    // Trigger CSS transition on next frame
    requestAnimationFrame(() => {
      requestAnimationFrame(() => { lbEl.classList.add('is-open'); });
    });

    lbIsOpen = true;
    document.body.style.overflow = 'hidden';
    lbEl.querySelector('.cpc-lightbox__close').focus();
  }

  function closeLightbox() {
    if (!lbEl || !lbIsOpen) return;
    lbEl.classList.remove('is-open');
    lbIsOpen = false;
    document.body.style.overflow = '';

    // Re-focus the image wrap after animation completes
    setTimeout(() => {
      if (!lbIsOpen) lbEl.hidden = true;
      dom.mainWrap?.focus();
    }, 300);
  }

  function attachLightboxListener() {
    if (!dom.mainWrap) return;

    // Make the image wrap focusable for keyboard users
    dom.mainWrap.setAttribute('tabindex', '0');
    dom.mainWrap.setAttribute('role', 'button');
    dom.mainWrap.setAttribute('aria-label', 'View full screen');

    dom.mainWrap.addEventListener('click', () => {
      openLightbox(activeImageIndex);
    });

    // Also open on Enter / Space for keyboard accessibility
    dom.mainWrap.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openLightbox(activeImageIndex);
      }
    });
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  function init() {
    if (!document.getElementById('cpc-config')) return;

    resolveDOM();
    initFromURL();
    attachToggleListeners();
    attachATCListener();
    attachPetNameListeners();
    attachFileUploadListeners();
    attachSwipeListener();
    attachThumbHeightSync();
    attachLightboxListener();

    updateAll({ replace: true });
  }

}());